import { useRecoilState, useRecoilValue } from "recoil";
import { plusDataAtom, userAtom } from "@state/State";
import { IPlusUsageData, IUser } from "@interfaces/Interfaces";
import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as DatabaseService from "@controllers/DatabaseService";
import DataController from "@controllers/DataController";
import { USAGE_DATA_REFRESH_INTERVAL } from "../../Constants";
import { useLoggedIn } from "@hooks/auth/useLoggedIn";
import _ from "lodash";

type RefreshJRCALCPlusData = {
  refreshJRCALCPlusData: (user: IUser) => Promise<void>;
};

const fetchJRCALCPlusData = async (user: IUser): Promise<IPlusUsageData[]> => {
  if (!user || !user.userData) {
    throw new Error("User is required");
  }

  const data = (await DatabaseService.getJRCALCPLusTimelineData(user)) ?? [];
  return data;
};

export const useRefreshJRCALCPlusData = (shouldLoad?: boolean): RefreshJRCALCPlusData => {
  const user = useRecoilValue(userAtom);
  const [plusData, setPlusData] = useRecoilState<IPlusUsageData[] | null>(plusDataAtom);
  const loggedIn = useLoggedIn();
  const queryClient = useQueryClient();

  const isEnabled = Boolean(loggedIn && user && user.userData && shouldLoad);

  const { data: refreshedData } = useQuery({
    queryKey: ["jrcalc-plus-data", user?.userData?.contactID],
    queryFn: () => fetchJRCALCPlusData(user!),
    enabled: isEnabled,
    refetchInterval: isEnabled ? USAGE_DATA_REFRESH_INTERVAL : false,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!user || !user.userData) {
      queryClient.removeQueries({ queryKey: ["jrcalc-plus-data"] });
    }
  }, [user, queryClient]);

  useEffect(() => {
    if (refreshedData) {
      const isEqual = _.isEqual(plusData, refreshedData);

      if (!isEqual) {
        DataController.saveJRCALCPlusTimelineData(refreshedData);
        setPlusData(refreshedData);
      }
    }
  }, [refreshedData, plusData, setPlusData]);

  const refreshJRCALCPlusData = useCallback(
    async (_user: IUser): Promise<void> => {
      try {
        if (!_user || !_user.userData) {
          return;
        }

        const data = await fetchJRCALCPlusData(_user);
        const isEqual = _.isEqual(plusData, data);

        if (!isEqual) {
          await DataController.saveJRCALCPlusTimelineData(data);
          setPlusData(data);
        }

        queryClient.invalidateQueries({ queryKey: ["jrcalc-plus-data", _user.userData.contactID] });
      } catch (error) {
        console.log(error);
      }
    },
    [plusData, setPlusData, queryClient]
  );

  return {
    refreshJRCALCPlusData,
  };
};
